/**
 * Calculadora de prazos (cálculo na hora, no navegador).
 *  - Disponibilização no DJEN → publicação no 1º dia útil seguinte → prazo começa no dia útil seguinte (CPC 224 §3; Lei 11.419 art. 4º).
 *  - Intimação pelo portal (expedição) → ciência no 10º dia corrido (Lei 11.419 art. 5º §3) → prazo começa no dia útil seguinte.
 *  - Dias úteis: só contam dias com expediente. Dias corridos: o último dia que cair sem expediente vai para o próximo dia útil (CPC 224 §1).
 * Modo manual: só fins de semana + "quantos feriados" informados. Modo automático: feriados/suspensões/recesso do sistema.
 */
export type CalcType = "uteis" | "corridos";
export interface CalcInput {
  expedicao?: string; disponibilizacao?: string; inicio?: string;
  dias: number; tipo: CalcType; dobro?: boolean;
  /** Manual: quantos dias sem expediente (feriados em dia de semana) caem no período. */
  feriadosExtras?: number;
  /** Prazo interno: quantos dias úteis antes do fatal. */
  interno?: number;
  /** Automático: dias sem expediente (data → motivo). */
  off?: Map<string, string>;
}
export interface CalcDay { date: string; n: number | null; reason?: string }
export interface CalcResult {
  ciencia?: string; publicacao?: string; inicio: string; fatal: string; interno: string | null;
  totalDias: number; days: CalcDay[]; notes: string[];
}

const DAY = 86_400_000;
const toD = (iso: string) => new Date(`${iso}T00:00:00Z`);
const toIso = (d: Date) => d.toISOString().slice(0, 10);
const add = (iso: string, n: number) => toIso(new Date(toD(iso).getTime() + n * DAY));
const WEEK = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
export const weekday = (iso: string) => WEEK[toD(iso).getUTCDay()]!;

function offReason(iso: string, off?: Map<string, string>): string | null {
  const wd = toD(iso).getUTCDay();
  if (wd === 0 || wd === 6) return wd === 0 ? "domingo" : "sábado";
  return off?.get(iso) ?? null;
}
const nextBiz = (iso: string, off?: Map<string, string>) => { let d = add(iso, 1); while (offReason(d, off)) d = add(d, 1); return d; };
const isoOk = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

export function calcDeadline(i: CalcInput): CalcResult | { error: string } {
  const notes: string[] = [];
  const N = Math.max(0, Math.floor(i.dias || 0)) * (i.dobro ? 2 : 1);
  if (!N) return { error: "Informe a quantidade de dias." };
  if (i.dobro) notes.push(`Prazo em dobro: ${i.dias} × 2 = ${N} dias.`);
  let ciencia: string | undefined, publicacao: string | undefined, sugerido: string | undefined;
  if (isoOk(i.disponibilizacao)) {
    publicacao = nextBiz(i.disponibilizacao!, i.off);
    sugerido = nextBiz(publicacao, i.off);
    notes.push("Disponibilização no DJEN: publicação no 1º dia útil seguinte; o prazo começa no dia útil depois da publicação.");
  } else if (isoOk(i.expedicao)) {
    ciencia = add(i.expedicao!, 10);
    if (offReason(ciencia, i.off)) ciencia = nextBiz(ciencia, i.off);
    sugerido = nextBiz(ciencia, i.off);
    notes.push("Intimação pelo portal: ciência no 10º dia corrido após a expedição (se não houver abertura antes); o prazo começa no dia útil seguinte.");
  }
  let inicio = isoOk(i.inicio) ? i.inicio! : sugerido;
  if (!inicio) return { error: "Informe a data da disponibilização, da expedição ou o início do prazo." };
  if (offReason(inicio, i.off)) { const r = offReason(inicio, i.off); inicio = nextBiz(inicio, i.off); notes.push(`O início caía em dia sem expediente (${r}); passou para ${inicio}.`); }

  const days: CalcDay[] = [];
  let fatal = inicio;
  if (i.tipo === "uteis") {
    let count = 0, d = inicio;
    for (let guard = 0; guard < 2000; guard++, d = add(d, 1)) {
      const r = offReason(d, i.off);
      if (r) { days.push({ date: d, n: null, reason: r }); continue; }
      count++; days.push({ date: d, n: count });
      if (count === N) break;
    }
    fatal = d;
    const extra = Math.max(0, Math.floor(i.feriadosExtras ?? 0));
    if (extra) {
      for (let k = 0; k < extra; k++) {
        let nx = add(fatal, 1);
        while (offReason(nx, i.off)) { days.push({ date: nx, n: null, reason: offReason(nx, i.off)! }); nx = add(nx, 1); }
        days.push({ date: nx, n: N + k + 1, reason: "dia a mais por feriado informado" }); fatal = nx;
      }
      notes.push(`${extra} feriado(s) informado(s) no período: +${extra} dia(s) útil(eis).`);
    }
  } else {
    for (let k = 0; k < N; k++) days.push({ date: add(inicio, k), n: k + 1 });
    fatal = add(inicio, N - 1);
    const r = offReason(fatal, i.off);
    if (r) {
      const nx = nextBiz(fatal, i.off);
      days.push({ date: nx, n: null, reason: `prorrogado: o último dia caiu em ${r}` });
      notes.push(`O último dia (${fatal}) caiu em ${r}: prorrogado para o próximo dia útil.`);
      fatal = nx;
    }
    if (i.feriadosExtras) notes.push("Em dias corridos os feriados no meio do prazo não alteram a contagem; só o último dia é prorrogado.");
  }
  let interno: string | null = null;
  const back = Math.max(0, Math.floor(i.interno ?? 0));
  if (back) {
    let d = fatal;
    for (let k = 0; k < back; k++) { d = add(d, -1); while (offReason(d, i.off)) d = add(d, -1); }
    interno = d;
  }
  return { ciencia, publicacao, inicio, fatal, interno, totalDias: N, days, notes };
}
